import React from 'react';
import LazyProductImage from './LazyProductImage.tsx';
import analyticsService from '../services/AnalyticsService.ts';
import FrustrationDetectionService from '../services/FrustrationDetectionService.ts';
import './ProductTile.css';
import sativaBadge from '../lineage-sativa-badge-03.png';
import indicaBadge from '../lineage-indica-badge-03.png';
import hybridBadge from '../lineage-hybrid-badge-03.png';
import sativaHybridBadge from '../lineage-sativa-hybrid-badge-03.png';
import indicaHybridBadge from '../lineage-indica-hybrid-badge-03.png';
import cbdBadge from '../lineage-cbd-badge-03.png';
import staffPickBadge from '../staff-pick-badge-03.png';

// Type definitions
interface MenuProduct {
  id: string;
  name: string;
  description?: string;
  category: string;
  subcategory?: string;
  brand?: {
    id: string;
    name: string;
    description?: string;
  };
  images: Array<{ url: string }>;
  strainType?: string;
  potencyThc?: {
    formatted: string;
    range?: number[];
    unit?: string;
  };
  potencyCbd?: {
    formatted: string;
    range?: number[];
    unit?: string;
  };
  terpenes?: Array<{
    id: string;
    name: string;
    description?: string;
    value: number;
    unitSymbol?: string;
  }>;
  cannabinoids?: Array<{
    id: string;
    name: string;
    description?: string;
    value: number;
    unitSymbol?: string;
  }>;
  variants: Array<{
    id: string;
    option: string;
    priceMed: number | null;
    priceRec: number | null;
    specialPriceMed: number | null;
    specialPriceRec: number | null;
    specialRewardIds?: string[];
    inventoryLevel?: number;
    quantity?: number;
    flowerEquivalent?: {
      value: number;
    };
  }>;
  staffPick?: boolean;
  tags?: string[];
  menuTypes?: string[];
  effects?: string[];
  flavors?: string[];
  createdAt?: string;
  enterpriseProductId?: string;
  productBatchId?: string;
  slug?: string;
  posId?: string;
}

interface ProductTileV3Props {
  product: MenuProduct;
  variant: any;
  animationClass: string;
  tileId?: string;
  pricingType: 'MEDICAL' | 'RECREATIONAL';
  menuId?: string;
  selectedCategory?: string;
  isVermontStore?: boolean;
  debugMode?: boolean;
  cart: any;
  cartLoading: boolean;
  isAddingToCart: boolean;
  activeMarquees: Set<string>;
  stoppingMarquees: Set<string>;
  dealDescriptions?: Record<string, { title: string; description: string }>;
  productTileCursorStyle?: React.CSSProperties;
  isMobile?: boolean;
  isCompactMode?: boolean;
  selectedTerpenes?: string[];
  selectedCannabinoids?: string[];
  onProductClick: (product: MenuProduct) => void;
  onProductHover: (productId: string) => void;
  onProductLeave: (productId: string) => void;
  onAddToCart: (product: MenuProduct, variant: any) => void;
  onDebugClick: (product: MenuProduct) => void;
  onDealClick: (product: MenuProduct) => void;
  onVermontIncrement?: (product: MenuProduct, variant: any) => void;
  onVermontDecrement?: (product: MenuProduct, variant: any) => void;
}

// Initialize services
const frustrationService = FrustrationDetectionService.getInstance();

// Feature flags
const SHOW_TOTAL_TERPENES = false; // Set to true to show total terpenes display

// Helper functions
const stripAfterPipe = (str: string): string => {
  if (!str) return str;
  const pipeIndex = str.indexOf('|');
  return pipeIndex !== -1 ? str.substring(0, pipeIndex).trim() : str;
};

// Map terpene technical names to display names
const getTerpeneDisplayName = (terpeneName: string | null): string => {
  if (!terpeneName) return '';

  // Normalize terpene name by converting β to Beta and removing spaces/hyphens
  const normalizedName = terpeneName
    .replace(/β-?/g, 'Beta')           // Convert β or β- to Beta
    .replace(/\s+/g, '')              // Remove spaces
    .replace(/-/g, '');               // Remove hyphens

  const displayNameMap: { [key: string]: string } = {
    'AlphaPinene': 'α-Pinene',
    'BetaCaryophyllene': 'β-Caryophyllene',
    'BetaEudesmol': 'β-Eudesmol',
    'BetaMyrcene': 'β-Myrcene',
    'BetaPinene': 'β-Pinene',
    'Bisabolol': 'Bisabolol',
    'CaryophylleneOxide': 'Caryophyllene Oxide',
    'Eucalyptol': 'Eucalyptol',
    'Humulene': 'Humulene',
    'Limonene': 'Limonene',
    'Linalool': 'Linalool',
    'Terpinolene': 'Terpinolene',
    'TransNerolidol': 'Trans-Nerolidol'
  };

  // Try normalized name first, then fall back to original
  return displayNameMap[normalizedName] || displayNameMap[terpeneName] || terpeneName;
};

// Regex to filter out THC-related cannabinoids (case-insensitive, flexible spacing and formatting)
const THC_FILTER_REGEX = /\bTHC(?:\s*[-_]?\s*[A-Z]?\d*(?:\.\d+)?[A-Z]*)*\b/i;

// Helper function to check if a cannabinoid is THC-related and should be filtered out
const isTHCRelatedCannabinoid = (cannabinoidName: string): boolean => {
  return THC_FILTER_REGEX.test(cannabinoidName);
};

// Map cannabinoid technical names to display names for better user understanding
const getCannabinoidDisplayName = (cannabinoidName: string): string => {
  if (!cannabinoidName) return '';

  // Remove extra whitespace and normalize case
  const cleanName = cannabinoidName.trim();

  // If it's a THC-related compound, filter it out by returning empty string
  if (isTHCRelatedCannabinoid(cleanName)) {
    return '';
  }

  // Create a simplified version for mapping (remove spaces, hyphens, convert to lowercase)
  const simpleName = cleanName.replace(/\s+/g, '').replace(/-/g, '').toLowerCase();

  // Mapping for better display names
  const displayNameMap: { [key: string]: string } = {
    'cbc': 'CBC',
    'cbg': 'CBG',
    'cbn': 'CBN',
    'cbga': 'CBG-A',
    'cbca': 'CBC-A',
    'cbna': 'CBN-A',
    'cbgv': 'CBG-V',
    'cbcv': 'CBC-V',
    'cbdv': 'CBD-V',
    'cbda': 'CBD-A',
    'cbdva': 'CBD-VA',
    'thcv': 'THC-V',
    'thcva': 'THC-VA',
    'thca': 'THC-A'
  };

  return displayNameMap[simpleName] || displayNameMap[cannabinoidName] || simpleName;
};

const formatCannabinoidValue = (value: number, unit?: string | null): string => {
  const formatted = value.toFixed(2);
  switch (unit) {
    case 'MILLIGRAMS': return `${formatted}mg`;
    case 'MILLIGRAMS_PER_GRAM': return `${formatted}mg/g`;
    case 'PERCENTAGE':
    default: return `${formatted}%`;
  }
};

const sanitizeBrandName = (brandName: string): string => {
  if (!brandName || typeof brandName !== 'string') return '';

  const sanitized = brandName
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[\r\n\t]/g, ' ')
    .trim();

  return sanitized || '';
};

const isValidForDisplay = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const trimmedValue = value.trim().toLowerCase();
  return trimmedValue !== '' && trimmedValue !== 'n/a' && trimmedValue !== 'not_applicable';
};

const formatStrainType = (strainType: string): string => {
  if (!strainType) return '';
  if (strainType === 'NOT_APPLICABLE') return '';
  if (strainType === 'ONE_TO_ONE') return '1:1';
  if (strainType === 'SATIVA_HYBRID') return 'Sativa-Hybrid';
  if (strainType === 'INDICA_HYBRID') return 'Indica-Hybrid';
  if (strainType === 'HIGH_CBD') return 'High CBD';

  // Handle underscore-separated values generically
  if (strainType.includes('_')) {
    return strainType.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  return strainType.toLowerCase().replace(/^\w/, c => c.toUpperCase());
};

const getStrainTypeBadge = (strainType: string | undefined): string | null => {
  if (!strainType) return null;
  if (strainType === 'NOT_APPLICABLE') return null;
  if (strainType === 'ONE_TO_ONE') return cbdBadge;

  const normalizedType = strainType.toUpperCase();

  if (normalizedType === 'SATIVA') return sativaBadge;
  if (normalizedType === 'INDICA') return indicaBadge;
  if (normalizedType === 'SATIVA_HYBRID') return sativaHybridBadge;
  if (normalizedType === 'INDICA_HYBRID') return indicaHybridBadge;
  if (normalizedType === 'HYBRID') return hybridBadge;

  return null;
};

const sanitizeCategoryName = (categoryName: string): string => {
  if (!categoryName) return categoryName;
  if (categoryName === 'all') return 'All';

  return categoryName
    .trim()
    .replace(/[^\w\s-_]/g, '')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const shouldDisplayTHC = (product: MenuProduct): boolean => {
  if (!product || !product.category) return false;

  const sanitizedCategory = sanitizeCategoryName(product.category).toLowerCase();

  const thcCategories = [
    'flower',
    'vaporizer', 'vape', 'cartridge', 'cart',
    'concentrate',
    'edible',
    'pre roll', 'pre-roll', 'preroll', 'pre rolls', 'pre-rolls', 'prerolls'
  ];

  for (const thcCategory of thcCategories) {
    if (
      sanitizedCategory === thcCategory ||
      sanitizedCategory.includes(thcCategory)
    ) {
      return true;
    }
  }

  if (product.subcategory) {
    const sanitizedSubcategory = product.subcategory.toLowerCase();
    const preRollKeywords = ['pre roll', 'pre-roll', 'preroll', 'pre rolls', 'pre-rolls', 'prerolls'];
    for (const keyword of preRollKeywords) {
      if (sanitizedSubcategory.includes(keyword)) {
        return true;
      }
    }
  }

  return false;
};

const hasValidTHCData = (product: MenuProduct): boolean => {
  return !!(
    product &&
    product.potencyThc &&
    isValidForDisplay(product.potencyThc.formatted) &&
    product.potencyThc.formatted !== '0%' &&
    product.potencyThc.formatted !== '0.0%'
  );
};

const hasValidCBDData = (product: MenuProduct): boolean => {
  return !!(
    product &&
    product.potencyCbd &&
    isValidForDisplay(product.potencyCbd.formatted) &&
    product.potencyCbd.formatted !== '0%' &&
    product.potencyCbd.formatted !== '0.0%'
  );
};

const isEffectivelyZero = (value: number | null | undefined): boolean => {
  return value === null || value === undefined || Math.abs(value) < 0.005;
};

const getTotalTerpenePercentage = (product: MenuProduct): number => {
  if (!product.terpenes || product.terpenes.length === 0) return 0;

  return product.terpenes.reduce((total, terpene) => {
    if (terpene.value && !isEffectivelyZero(terpene.value)) {
      return total + terpene.value;
    }
    return total;
  }, 0);
};

const hasValidTerpeneData = (product: MenuProduct): boolean => {
  const totalTerpenes = getTotalTerpenePercentage(product);
  return totalTerpenes > 0;
};

const isPreRoll = (product: MenuProduct): boolean => {
  if (!product || !product.category) return false;

  const sanitizedCategory = sanitizeCategoryName(product.category).toLowerCase();
  const preRollKeywords = ['pre roll', 'pre-roll', 'preroll', 'pre rolls', 'pre-rolls', 'prerolls'];

  // Check category
  for (const keyword of preRollKeywords) {
    if (sanitizedCategory.includes(keyword)) {
      return true;
    }
  }

  // Check subcategory
  if (product.subcategory) {
    const sanitizedSubcategory = product.subcategory.toLowerCase();
    for (const keyword of preRollKeywords) {
      if (sanitizedSubcategory.includes(keyword)) {
        return true;
      }
    }
  }

  return false;
};

const shouldDisplayWeight = (product: MenuProduct): boolean => {
  if (!product || !product.category) return false;

  const sanitizedCategory = sanitizeCategoryName(product.category).toLowerCase();
  const excludedCategories = ['edible', 'edibles', 'accessory', 'accessories', 'apparel', 'topical', 'topicals'];

  for (const excludedCategory of excludedCategories) {
    if (sanitizedCategory === excludedCategory || sanitizedCategory.includes(excludedCategory)) {
      return false;
    }
  }

  return true;
};

const formatWeight = (weight: string): string => {
  if (!weight || weight === 'all') return weight;

  if (weight.startsWith('.')) {
    return '0' + weight;
  }

  return weight;
};

const parseWeightFromOption = (option: string): number | null => {
  if (!option) return null;

  const cleanOption = option.toLowerCase().trim();

  const gramMatch = cleanOption.match(/^(\d+(?:\.\d+)?)g?$/);
  if (gramMatch) {
    return parseFloat(gramMatch[1]);
  }

  const ozMatch = cleanOption.match(/^(\d+)\/(\d+)(?:oz)?$/) || cleanOption.match(/^(\d+)(?:oz)?$/);
  if (ozMatch) {
    if (ozMatch[2]) {
      const numerator = parseFloat(ozMatch[1]);
      const denominator = parseFloat(ozMatch[2]);
      return (numerator / denominator) * 28;
    } else {
      return parseFloat(ozMatch[1]) * 28;
    }
  }

  return null;
};

const getSortedVariants = (product: MenuProduct): any[] => {
  if (!product.variants || product.variants.length === 0) {
    return [];
  }

  return [...product.variants].sort((a, b) => {
    const weightA = a.flowerEquivalent?.value || parseWeightFromOption(a.option) || 0;
    const weightB = b.flowerEquivalent?.value || parseWeightFromOption(b.option) || 0;
    return weightA - weightB;
  });
};

const createRangeDisplay = (variants: any[]): string => {
  if (variants.length <= 1) {
    return variants.map(v => formatWeight(v.option).toUpperCase()).join('');
  }

  const firstVariant = formatWeight(variants[0].option).toUpperCase();
  const lastVariant = formatWeight(variants[variants.length - 1].option).toUpperCase();

  return `${firstVariant} - ${lastVariant}`;
};

const getWeightDisplayText = (product: MenuProduct, menuId?: string, variant?: any): string => {
  if (!shouldDisplayWeight(product) || !product.variants || product.variants.length === 0) {
    return '';
  }

  const isVermontStore = menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC;

  if (isVermontStore) {
    const category = product.category?.toLowerCase();
    if (category === 'flower' || category === 'concentrates') {
      if (product.variants.length === 1) {
        const variant = product.variants[0];
        return formatWeight(variant.option).toUpperCase();
      } else {
        const sortedVariants = getSortedVariants(product);
        if (category === 'flower') {
          return createRangeDisplay(sortedVariants);
        } else {
          return sortedVariants
            .map(v => formatWeight(v.option).toUpperCase())
            .join(', ');
        }
      }
    } else {
      const variant = product.variants[0];
      return variant ? formatWeight(variant.option) : '';
    }
  }

  const selectedVariant = variant || product.variants[0];
  return selectedVariant ? formatWeight(selectedVariant.option) : '';
};

const getPotencyDisplayText = (
  potency: { formatted: string; range?: number[]; unit?: string } | null | undefined,
  variant: any,
  product: MenuProduct
): string => {
  // Return empty if no potency data
  if (!potency || !potency.formatted) {
    return '';
  }

  // If the formatted value doesn't contain a range, return it as-is
  if (!potency.formatted.includes(' - ') && !potency.formatted.includes('–')) {
    return potency.formatted;
  }

  // If we have a range and range array exists, extract the specific value for this variant
  if (potency.range && potency.range.length > 0 && variant) {
    const sortedVariants = getSortedVariants(product);
    const variantIndex = sortedVariants.findIndex(v => v.id === variant.id);

    if (variantIndex !== -1 && variantIndex < potency.range.length) {
      const value = potency.range[variantIndex];
      return `${value}%`;
    }
  }

  // Fallback: return the formatted value (shouldn't normally reach here)
  return potency.formatted;
};

const hasRewards = (product: MenuProduct): boolean => {
  return product.variants.some(variant =>
    variant.specialRewardIds && variant.specialRewardIds.length > 0
  );
};

const hasSpecialPrices = (product: MenuProduct): boolean => {
  return product.variants.some(variant =>
    variant.specialPriceMed !== null || variant.specialPriceRec !== null
  );
};

const hasDeals = (product: MenuProduct): boolean => {
  return hasRewards(product) || hasSpecialPrices(product);
};

const getRegularPrice = (variant: any, pricingType: 'MEDICAL' | 'RECREATIONAL'): number | null => {
  return pricingType === 'MEDICAL' ? variant.priceMed : variant.priceRec;
};

const getSpecialPrice = (variant: any, pricingType: 'MEDICAL' | 'RECREATIONAL'): number | null => {
  return pricingType === 'MEDICAL' ? variant.specialPriceMed : variant.specialPriceRec;
};

const hasSpecialPricing = (variant: any, pricingType: 'MEDICAL' | 'RECREATIONAL'): boolean => {
  const regularPrice = getRegularPrice(variant, pricingType);
  const specialPrice = getSpecialPrice(variant, pricingType);
  return regularPrice !== null && specialPrice !== null && specialPrice < regularPrice;
};

const calculateDiscount = (regularPrice: number, specialPrice: number): number => {
  if (regularPrice <= 0) return 0;
  return Math.round(((regularPrice - specialPrice) / regularPrice) * 100);
};

const getCartQuantityForProduct = (product: MenuProduct, cart: any): number => {
  if (!cart || !cart.items || !product) {
    return 0;
  }

  const cartItem = cart.items.find((item: any) => item.productId === product.id);

  if (!cartItem) {
    return 0;
  }

  const variant = product.variants?.find(v => v.option === cartItem.option);
  if (variant && variant.flowerEquivalent && variant.flowerEquivalent.value) {
    return variant.flowerEquivalent.value * cartItem.quantity;
  } else if (variant && parseWeightFromOption(variant.option)) {
    return (parseWeightFromOption(variant.option) || 0) * cartItem.quantity;
  } else {
    return cartItem.quantity;
  }
};

const isProductInCart = (product: MenuProduct, cart: any): boolean => {
  return getCartQuantityForProduct(product, cart) > 0;
};

const findCartItemForProduct = (product: MenuProduct, cart: any): any => {
  if (!cart || !cart.items || !product) {
    return null;
  }
  return cart.items.find((item: any) => item.productId === product.id);
};

const getCurrentVariantFromCart = (product: MenuProduct, cart: any): any => {
  const cartItem = findCartItemForProduct(product, cart);
  if (!cartItem || !cartItem.option) return null;

  return product.variants?.find(v => v.option === cartItem.option) || null;
};

const getNextVariant = (product: MenuProduct, currentVariant: any): any => {
  const sortedVariants = getSortedVariants(product);
  if (sortedVariants.length === 0) return null;

  if (!currentVariant) {
    return sortedVariants[0];
  }

  const currentIndex = sortedVariants.findIndex(v => v.id === currentVariant.id);

  if (currentIndex === sortedVariants.length - 1) {
    return sortedVariants[currentIndex];
  }

  return currentIndex >= 0 && currentIndex < sortedVariants.length - 1
    ? sortedVariants[currentIndex + 1]
    : sortedVariants[sortedVariants.length - 1];
};

const getPreviousVariant = (product: MenuProduct, currentVariant: any): any => {
  const sortedVariants = getSortedVariants(product);
  if (sortedVariants.length === 0 || !currentVariant) return null;

  const currentIndex = sortedVariants.findIndex(v => v.id === currentVariant.id);

  if (currentIndex === 0) {
    return null;
  }

  return currentIndex > 0
    ? sortedVariants[currentIndex - 1]
    : null;
};

// Component: Deal Triangle (V3 solid triangle corner style)
const DealTriangle: React.FC<{
  product: MenuProduct;
  pricingType: 'MEDICAL' | 'RECREATIONAL';
  onClick: (e: React.MouseEvent) => void;
  dealInfo?: { title: string; description: string };
}> = ({ product, pricingType, onClick, dealInfo }) => {
  const hasRewardIds = hasRewards(product);

  const specialPricingVariant = product.variants.find(variant =>
    hasSpecialPricing(variant, pricingType)
  );

  // For rewards-only (no discount percentage) - show "DEAL"
  if (hasRewardIds && !specialPricingVariant) {
    const cleanDescription = dealInfo?.description?.replace(/\n+/g, '\n').trim() || '';
    const tooltipText = dealInfo ? `${dealInfo.title.toUpperCase()}\n\n${cleanDescription}` : "VIEW DEAL DETAILS";

    return (
      <button
        className="deal-triangle-v3 deal-triangle-tooltip"
        onClick={onClick}
        aria-label="View product deals"
        data-tooltip={tooltipText}
      >
        <span className="deal-triangle-text">
          <span className="deal-percent">DEAL</span>
        </span>
      </button>
    );
  }

  // For percentage discounts - show "XX%" and "OFF" on separate lines
  if (specialPricingVariant) {
    const regularPrice = getRegularPrice(specialPricingVariant, pricingType);
    const specialPrice = getSpecialPrice(specialPricingVariant, pricingType);

    if (regularPrice && specialPrice) {
      const discount = calculateDiscount(regularPrice, specialPrice);

      return (
        <div
          className="deal-triangle-v3"
          aria-label={`${discount}% off regular price`}
        >
          <span className="deal-triangle-text">
            <span className="deal-percent">{discount}%</span>
            <span className="deal-off">OFF</span>
          </span>
        </div>
      );
    }
  }

  return null;
};

// Component: Vermont Increment Selector
const VermontIncrementSelector: React.FC<{
  product: MenuProduct;
  variant: any;
  pricingType: 'MEDICAL' | 'RECREATIONAL';
  onIncrement: () => void;
  onDecrement: () => void;
  isDisabled: boolean;
  isLoading: boolean;
  cart: any;
}> = ({ product, variant, pricingType, onIncrement, onDecrement, isDisabled, isLoading, cart }) => {
  const currentVariant = getCurrentVariantFromCart(product, cart);
  const sortedVariants = getSortedVariants(product);

  const currentIndex = currentVariant
    ? sortedVariants.findIndex(v => v.id === currentVariant.id)
    : -1;

  const atMaxVariant = currentIndex >= sortedVariants.length - 1;
  const atMinVariant = currentIndex <= 0;

  let displayText = '';
  if (currentVariant) {
    const isFlowerProduct = product.category?.toLowerCase() === 'flower';
    const quantity = getCartQuantityForProduct(product, cart);

    if (isFlowerProduct) {
      displayText = `${quantity}G`;
    } else {
      displayText = currentVariant.option.toUpperCase();
    }
  }

  const variantPrice = currentVariant
    ? (pricingType === 'MEDICAL' ? currentVariant.priceMed : currentVariant.priceRec)
    : 0;

  if (isLoading) {
    return (
      <button className="add-to-cart-btn-v3 adding" disabled>
        ADDING...
      </button>
    );
  }

  return (
    <div className="add-to-cart-btn-v3 vermont-increment-selector">
      <button
        className="vermont-btn vermont-decrement"
        onClick={(e) => {
          e.stopPropagation();
          onDecrement();
        }}
        disabled={isDisabled || atMinVariant}
      >
        −
      </button>
      <div className="vermont-display" onClick={(e) => e.stopPropagation()}>
        <span className="add-text">ADDED<span className="vermont-dash"> — </span></span>
        <span className="add-text-mobile">ADDED<span className="vermont-dash"> — </span></span>
        <span className="vermont-mobile-space">&nbsp;</span>
        <span className="vermont-quantity">{displayText}</span>
        <span className="vermont-mobile-space">&nbsp;</span>
        <span className="vermont-price">
          <span className="vermont-dash"> — </span>
          ${variantPrice ? variantPrice.toFixed(2) : '0.00'}
        </span>
      </div>
      <button
        className="vermont-btn vermont-increment"
        onClick={(e) => {
          e.stopPropagation();
          onIncrement();
        }}
        disabled={isDisabled || atMaxVariant}
      >
        +
      </button>
    </div>
  );
};

// Plus icon for compact/overflow button state — matches Figma exactly
const PlusIcon = () => (
  <svg
    className="plus-icon-v3"
    width="16"
    height="16"
    viewBox="0 0 20.25 20.25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M10.125 0.5V19.75M0.5 10.125H19.75" stroke="#00443B" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Component: Add To Cart Button
const AddToCartButton: React.FC<{
  product: MenuProduct;
  variant: any;
  pricingType: 'MEDICAL' | 'RECREATIONAL';
  isAdding: boolean;
  isDisabled: boolean;
  onClick: (e: React.MouseEvent) => void;
  isVermontStore?: boolean;
  isCompactMode?: boolean;
  cart: any;
}> = ({ product, variant, pricingType, isAdding, isDisabled, onClick, isVermontStore, isCompactMode, cart }) => {
  const regularPrice = getRegularPrice(variant, pricingType);
  const specialPrice = getSpecialPrice(variant, pricingType);
  const hasSpecialPrice = hasSpecialPricing(variant, pricingType);

  const isAdded = isProductInCart(product, cart);
  const cartQuantity = getCartQuantityForProduct(product, cart);

  // Overflow detection for button text - measure text width vs available space
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [isOverflowingDeal, setIsOverflowingDeal] = React.useState(false);

  React.useEffect(() => {
    const checkOverflow = () => {
      if (buttonRef.current) {
        const btn = buttonRef.current;
        const style = window.getComputedStyle(btn);
        const paddingLeft = parseFloat(style.paddingLeft) || 0;
        const paddingRight = parseFloat(style.paddingRight) || 0;
        const availableWidth = btn.clientWidth - paddingLeft - paddingRight;

        // Use canvas to measure text width
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
          // Single price: all tiles switch together at this threshold
          const singleWidth = ctx.measureText('ADD TO CART — $199.99').width;
          setIsOverflowing(singleWidth > availableWidth);
          // Two prices: deal tiles switch earlier when strikethrough + sale doesn't fit
          const dealWidth = ctx.measureText('ADD TO CART — $199.99 $199.99').width;
          setIsOverflowingDeal(dealWidth > availableWidth);
        }
      }
    };

    // Delay check slightly to allow layout to settle after grid changes
    const rafId = requestAnimationFrame(checkOverflow);
    const observer = new ResizeObserver(checkOverflow);
    if (buttonRef.current) observer.observe(buttonRef.current);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [product.id]);

  if (isAdding) {
    return (
      <button className="add-to-cart-btn-v3 adding" disabled={isDisabled}>
        ADDING...
      </button>
    );
  }

  if (isAdded) {
    const isFlowerProduct = product.category?.toLowerCase() === 'flower';
    return (
      <button className="add-to-cart-btn-v3 added" disabled={isDisabled}>
        {isVermontStore && isFlowerProduct ? `${cartQuantity}G ADDED` : 'ADDED'}
      </button>
    );
  }

  // Compact mode: always show plus icon with prices
  if (isCompactMode) {
    return (
      <button
        className="add-to-cart-btn-v3"
        onClick={onClick}
        disabled={isDisabled}
      >
        <span className="add-text"><PlusIcon /> </span>
        <span className="add-text-mobile"><PlusIcon /> </span>
        {hasSpecialPrice && regularPrice && specialPrice ? (
          <span className="price-container">
            <span className="regular-price-strike">${regularPrice.toFixed(2)}</span>
            <span className="special-price-highlight">${specialPrice.toFixed(2)}</span>
          </span>
        ) : (
          <>${regularPrice?.toFixed(2) || '0.00'}</>
        )}
      </button>
    );
  }

  // Normal mode: show full text and both prices if on discount
  if (hasSpecialPrice && regularPrice && specialPrice) {
    return (
      <button
        className="add-to-cart-btn-v3"
        onClick={onClick}
        disabled={isDisabled}
      >
        <span className="add-text"><PlusIcon /> </span>
        <span className="add-text-mobile"><PlusIcon /> </span>
        <span className="price-container">
          <span className="regular-price-strike">${regularPrice.toFixed(2)}</span>
          <span className="special-price-highlight">${specialPrice.toFixed(2)}</span>
        </span>
      </button>
    );
  }

  return (
    <button
      className="add-to-cart-btn-v3"
      onClick={onClick}
      disabled={isDisabled}
    >
      <span className="add-text">{isOverflowing ? <><PlusIcon /> </> : 'ADD TO CART — '}</span>
      <span className="add-text-mobile"><PlusIcon /> </span>
      ${regularPrice?.toFixed(2) || '0.00'}
    </button>
  );
};

// Main ProductTileV3 Component
const ProductTileV3: React.FC<ProductTileV3Props> = ({
  product,
  variant,
  animationClass,
  tileId,
  pricingType,
  menuId,
  selectedCategory,
  isVermontStore,
  debugMode,
  cart,
  cartLoading,
  isAddingToCart,
  activeMarquees,
  stoppingMarquees,
  dealDescriptions,
  productTileCursorStyle,
  isMobile,
  isCompactMode,
  selectedTerpenes = [],
  selectedCannabinoids = [],
  onProductClick,
  onProductHover,
  onProductLeave,
  onAddToCart,
  onDebugClick,
  onDealClick,
  onVermontIncrement,
  onVermontDecrement
}) => {
  // Get strain badge info
  const strainBadgeImage = getStrainTypeBadge(product.strainType);
  const strainAltText = `${formatStrainType(product.strainType || '')} strain`;

  return (
    <div
      className={`product-tile product-tile-v3 ${animationClass} ${isMobile ? 'mobile-layout' : ''}`}
      data-tile-id={tileId}
      onClick={() => onProductClick(product)}
      onMouseEnter={() => onProductHover(product.id)}
      onMouseLeave={() => onProductLeave(product.id)}
      style={productTileCursorStyle}
    >
      {/* Image Section */}
      <div className="product-image-section-v3">
        {product.images[0] ? (
          <LazyProductImage
            src={product.images[0].url}
            alt={`${sanitizeBrandName(product.brand?.name || '')} ${product.name}`}
            className=""
            onError={(e) => {
              analyticsService.trackEvent('product_image_error', {
                product_id: product.id,
                product_name: product.name,
                image_url: product.images[0]?.url || '',
                brand: product.brand?.name || '',
                category: product.category || '',
                store_id: menuId || 'unknown'
              });

              frustrationService.markSessionReplayPoint('product_image_error', {
                product_id: product.id,
                product_name: product.name,
                image_url: product.images[0]?.url || '',
                error_count: frustrationService.getFrustrationScore()
              });
            }}
          />
        ) : (
          <div className="no-image-placeholder">
            <span>No image</span>
          </div>
        )}

        {/* DEBUG MODE - Debug button overlay */}
        {debugMode && (
          <button
            className="debug-button"
            onClick={(e) => {
              e.stopPropagation();
              onDebugClick(product);
            }}
            aria-label="Debug product data"
          >
            DEBUG
          </button>
        )}

        {/* DEAL MODE - Deal ribbon overlay (top-left corner) */}
        {hasDeals(product) && (() => {
          const promotionId = `${product.id}-deal`;
          const promotionName = hasRewards(product) ? 'reward-deal' : 'discount-deal';

          if (!window.__VIEWED_PROMOTIONS) window.__VIEWED_PROMOTIONS = new Set();
          if (!window.__VIEWED_PROMOTIONS.has(promotionId)) {
            window.__VIEWED_PROMOTIONS.add(promotionId);

            analyticsService.trackEvent('view_promotion', {
              promotion_id: promotionId,
              promotion_name: promotionName,
              creative_name: 'deal_ribbon',
              creative_slot: 'product_overlay',
              location_id: selectedCategory,
              items: [{
                item_id: product.id,
                item_name: product.name,
                item_category: product.category,
                item_brand: product.brand?.name || '',
                price: pricingType === 'MEDICAL' ? variant.priceMed : variant.priceRec
              }],
              store_id: menuId || 'unknown'
            });
          }

          return (
            <DealTriangle
              product={product}
              pricingType={pricingType}
              onClick={(e) => {
                e.stopPropagation();
                onDealClick(product);
              }}
              dealInfo={dealDescriptions?.[product.id]}
            />
          );
        })()}
      </div>

      {/* Details Section - V3 has badges on the right side */}
      <div
        className="product-details-section-v3"
        style={{ borderBottom: 'none' }}
      >
        <div className="product-details-content-v3">
          {/* Left side: text content */}
          <div className="product-details-text-v3">
            {isValidForDisplay(product.name) && (
              <div className="product-detail-row product-title-row">
                <span
                  id={`strain-${product.id}`}
                  className={`product-detail-value ${
                    activeMarquees.has(`strain-${product.id}`) ? 'marquee-active' : ''
                  } ${
                    stoppingMarquees.has(`strain-${product.id}`) ? 'marquee-stopping' : ''
                  }`}
                >
                  {(activeMarquees.has(`strain-${product.id}`) || stoppingMarquees.has(`strain-${product.id}`)) ? (
                    <span
                      className="marquee-content"
                      data-text={stripAfterPipe(product.name)}
                    >
                      {stripAfterPipe(product.name)}
                    </span>
                  ) : (
                    (() => {
                      const isMobileView = isMobile || window.innerWidth <= 767;
                      const maxLength = isMobileView ? 44 : 29;
                      const cleanedName = stripAfterPipe(product.name);
                      return cleanedName.length > maxLength ? cleanedName.substring(0, maxLength).trim() + '...' : cleanedName;
                    })()
                  )}
                </span>
              </div>
            )}

            {product.brand && isValidForDisplay(product.brand.name) && (
              <div className="product-detail-row product-name-row">
                <span
                  id={`brand-${product.id}`}
                  className={`product-detail-value ${
                    activeMarquees.has(`brand-${product.id}`) ? 'marquee-active' : ''
                  } ${
                    stoppingMarquees.has(`brand-${product.id}`) ? 'marquee-stopping' : ''
                  }`}
                >
                  {(activeMarquees.has(`brand-${product.id}`) || stoppingMarquees.has(`brand-${product.id}`)) ? (
                    <span
                      className="marquee-content"
                      data-text={sanitizeBrandName(product.brand.name)}
                    >
                      {sanitizeBrandName(product.brand.name)}
                    </span>
                  ) : (
                    sanitizeBrandName(product.brand.name)
                  )}
                </span>
              </div>
            )}

            {/* For pre-rolls, show weight immediately after brand */}
            {isPreRoll(product) && shouldDisplayWeight(product) && getWeightDisplayText(product, menuId, variant) && (
              <div className="product-detail-row">
                {menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC ? (product.variants && product.variants.length > 1 ? 'WEIGHTS:' : 'WEIGHT:') : 'WEIGHT:'} <span className={`product-detail-value ${menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC && getWeightDisplayText(product, menuId, variant) === 'VARIABLE' ? 'weight-variable' : ''} ${menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC && product.category?.toLowerCase() === 'flower' && product.variants && product.variants.length > 1 ? 'weight-variants' : ''}`}>{getWeightDisplayText(product, menuId, variant)}</span>
              </div>
            )}

            {/* For edibles, show mg potency; for other products, show percentage potency */}
            {product.category?.toLowerCase() === 'edibles' ? (
              <>
                {((product.potencyThc?.formatted && product.potencyThc?.unit === 'mg') ||
                  (product.potencyCbd?.formatted && product.potencyCbd?.unit === 'mg')) && (
                  <div className="product-detail-row">
                    {product.potencyThc?.formatted && product.potencyThc?.unit === 'mg' && (
                      <>THC: <span className="product-detail-value">{product.potencyThc.formatted}</span></>
                    )}
                    {product.potencyThc?.formatted && product.potencyThc?.unit === 'mg' &&
                     product.potencyCbd?.formatted && product.potencyCbd?.unit === 'mg' && <>&nbsp; &nbsp;</>}
                    {product.potencyCbd?.formatted && product.potencyCbd?.unit === 'mg' && (
                      <>CBD: <span className="product-detail-value">{product.potencyCbd.formatted}</span></>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {shouldDisplayTHC(product) && (hasValidTHCData(product) || hasValidCBDData(product)) && (
                  <div className="product-detail-row">
                    {hasValidTHCData(product) && (
                      <>THC: <span className="product-detail-value">{getPotencyDisplayText(product.potencyThc, variant, product)}</span></>
                    )}
                    {hasValidTHCData(product) && hasValidCBDData(product) && <>&nbsp;</>}
                    {hasValidCBDData(product) && (
                      <>CBD: <span className="product-detail-value">{getPotencyDisplayText(product.potencyCbd, variant, product)}</span></>
                    )}
                  </div>
                )}

                {SHOW_TOTAL_TERPENES && hasValidTerpeneData(product) && (
                  <div className="product-detail-row">
                    {isMobile ? 'TERPENES:' : 'TOTAL TERPENES:'} <span className="product-detail-value">{getTotalTerpenePercentage(product).toFixed(2)}%</span>
                  </div>
                )}
              </>
            )}

            {/* Show weight at the bottom for non-preroll products */}
            {!isPreRoll(product) && shouldDisplayWeight(product) && getWeightDisplayText(product, menuId, variant) && (
              <div className="product-detail-row">
                {menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC ? (product.variants && product.variants.length > 1 ? 'WEIGHTS:' : 'WEIGHT:') : 'WEIGHT:'} <span className={`product-detail-value ${menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC && getWeightDisplayText(product, menuId, variant) === 'VARIABLE' ? 'weight-variable' : ''} ${menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC && product.category?.toLowerCase() === 'flower' && product.variants && product.variants.length > 1 ? 'weight-variants' : ''}`}>{getWeightDisplayText(product, menuId, variant)}</span>
              </div>
            )}
          </div>

          {/* Right side: badges - V3 moves badges from image to details section */}
          <div className="product-details-badges-v3">
            {/* STRAIN TYPE BADGE */}
            {strainBadgeImage && (
              <img
                src={strainBadgeImage}
                alt={strainAltText}
                className="strain-type-badge-v3"
              />
            )}

            {/* STAFF PICK BADGE */}
            {product.staffPick && (
              <img
                src={staffPickBadge}
                alt="Staff Pick"
                className="staff-pick-badge-v3"
              />
            )}
          </div>
        </div>
      </div>

      {/* Compound section positioned between details and button */}
      {(() => {
        // Calculate optimal box height based on selected compounds (not just those with data)
        // Since we now show "NONE" for missing data, every selected compound gets a line item
        const maxSelectedTerpeneCount = selectedTerpenes.length;
        const maxSelectedCannabinoidCount = selectedCannabinoids.length;

        // Calculate optimal height: compact sizing with padding
        const totalDataRows = maxSelectedTerpeneCount + maxSelectedCannabinoidCount;
        const titleRows = (selectedTerpenes.length > 0 ? 1 : 0) + (selectedCannabinoids.length > 0 ? 1 : 0);
        const spacingBetween = (selectedTerpenes.length > 0 && selectedCannabinoids.length > 0) ? 0.5 : 0;
        const topPadding = selectedTerpenes.length > 0 ? 0.75 : (selectedCannabinoids.length > 0 ? 0.75 : 0);
        const bottomPadding = 1.75; // Increased for more white space below data rows
        const optimalHeight = totalDataRows > 0 ? `${(totalDataRows + titleRows) * 1.2 + spacingBetween + topPadding + bottomPadding + 0.25}rem` : '0rem';

        return (
        <div
          className="mode-b-compound-section"
          style={{
            backgroundColor: '#FFFEF1',
            paddingLeft: '0.5rem',
            paddingRight: '0.25rem',
            height: optimalHeight,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            paddingTop: (selectedTerpenes.length > 0 || selectedCannabinoids.length > 0) ? (window.innerWidth <= 1286 ? '0rem' : '0.25rem') : '0rem',
            paddingBottom: (selectedTerpenes.length > 0 || selectedCannabinoids.length > 0) ? (window.innerWidth <= 1286 ? '1rem' : '0.125rem') : '0rem',
            overflow: 'hidden',
            transition: 'height 0.6s ease-out, padding 0.6s ease-out',
            position: 'relative',
          }}
        >
            {/* Terpene Data Display */}
            {selectedTerpenes.length > 0 && (
              <div style={{
                flex: selectedCannabinoids.length > 0 ? `0 0 auto` : '1 1 auto',
                overflow: 'hidden',
                paddingTop: '0.25rem',
                paddingBottom: selectedCannabinoids.length > 0 ? '0.5rem' : '0.75rem'
              }}>
                <div className="product-detail-row" style={{ marginTop: window.innerWidth <= 1286 ? '0.5rem' : '0.75rem', marginLeft: '0.5rem' }}>
                  TERPENES:
                </div>
                {selectedTerpenes.map(selectedName => {
                  const terpeneData = product.terpenes?.find(terpene =>
                    !isEffectivelyZero(terpene.value) &&
                    terpene.name &&
                    getTerpeneDisplayName(terpene.name) === selectedName
                  );
                  const hasData = !!terpeneData;

                  return (
                    <div
                      key={selectedName}
                      className="product-detail-row"
                      style={{
                        marginBottom: '0.15rem',
                        marginLeft: '1rem',
                        transition: 'all 0.4s ease-out',
                        fontSize: '0.875rem'
                      }}
                    >
                      <span style={{ display: 'inline-block' }}>
                        {selectedName.toUpperCase()}:
                      </span>
                      <span className="product-detail-value" style={{
                        color: hasData ? '#00443B' : '#999',
                        fontStyle: hasData ? 'normal' : 'italic'
                      }}>
                        {hasData ? `${terpeneData.value.toFixed(2)}%` : 'NONE'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Cannabinoid Data Display */}
            {selectedCannabinoids.length > 0 && (
              <div style={{
                flex: '0 0 auto',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                paddingBottom: '0.75rem'
              }}>
                <div className="product-detail-row" style={{ marginTop: selectedTerpenes.length > 0 ? (window.innerWidth <= 1286 ? '0.25rem' : '0') : (window.innerWidth <= 1286 ? '0.5rem' : '0.75rem'), marginLeft: '0.5rem' }}>
                  CANNABINOIDS:
                </div>
                {selectedCannabinoids.map(selectedName => {
                  const cannabinoidData = product.cannabinoids?.find(cannabinoid =>
                    !isEffectivelyZero(cannabinoid.value) &&
                    cannabinoid.name &&
                    !isTHCRelatedCannabinoid(cannabinoid.name) &&
                    getCannabinoidDisplayName(cannabinoid.name) === selectedName
                  );
                  const hasData = !!cannabinoidData;

                  return (
                    <div
                      key={selectedName}
                      className="product-detail-row"
                      style={{
                        marginBottom: '0.15rem',
                        marginLeft: '1rem',
                        transition: 'all 0.4s ease-out',
                        fontSize: '0.875rem'
                      }}
                    >
                      <span style={{ display: 'inline-block' }}>
                        {selectedName.toUpperCase()}:
                      </span>
                      <span className="product-detail-value" style={{
                        color: hasData ? '#00443B' : '#999',
                        fontStyle: hasData ? 'normal' : 'italic'
                      }}>
                        {hasData ? formatCannabinoidValue(cannabinoidData.value, cannabinoidData.unitSymbol) : 'NONE'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
        );
      })()}

      {/* Button Section */}
      <div className="product-button-section-v3">
        <AddToCartButton
          product={product}
          variant={variant}
          pricingType={pricingType}
          isAdding={isAddingToCart}
          isDisabled={isAddingToCart || cartLoading}
          isVermontStore={isVermontStore}
          isCompactMode={isCompactMode}
          cart={cart}
          onClick={(e) => {
            e.stopPropagation();
            // For Vermont store with multiple variants, open product drawer instead of adding to cart
            if (isVermontStore && product.variants && product.variants.length > 1) {
              onProductClick(product);
            } else {
              onAddToCart(product, variant);
            }
          }}
        />
      </div>

      {/* Mobile bottom row - badges and button in same flex container for vertical alignment */}
      {isMobile && (
        <div className="mobile-bottom-row-v3">
          <div className="mobile-badges-container-v3">
            {strainBadgeImage && (
              <img
                src={strainBadgeImage}
                alt={strainAltText}
                className="strain-type-badge-v3"
              />
            )}
            {product.staffPick && (
              <img
                src={staffPickBadge}
                alt="Staff Pick"
                className="staff-pick-badge-v3"
              />
            )}
          </div>
          <div className="mobile-button-container-v3">
            <AddToCartButton
              product={product}
              variant={variant}
              pricingType={pricingType}
              isAdding={isAddingToCart}
              isDisabled={isAddingToCart || cartLoading}
              isVermontStore={isVermontStore}
              isCompactMode={isCompactMode}
              cart={cart}
              onClick={(e) => {
                e.stopPropagation();
                if (isVermontStore && product.variants && product.variants.length > 1) {
                  onProductClick(product);
                } else {
                  onAddToCart(product, variant);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Extend window interface for promotions tracking
declare global {
  interface Window {
    __VIEWED_PROMOTIONS?: Set<string>;
  }
}

export default ProductTileV3;
